import { PortalUserState } from '@/common/components/ecorr-store/portal-user/portal-user.state';
import { ParadataEventService } from '@/common/paradata-event/paradata-event.service';
import { EcorrUtilsService } from '@/common/utils/ecorr-utils.service';
import { FileUtilService } from '@/common/utils/file-util.service';
import { SurveyViewsBaseComponent } from '@/surveys/components/survey-views-base/survey-views-base.component';
import { SurveyViewsBaseService } from '@/surveys/services/survey-views-base.service';
import { AddSurveyDialogService } from '@/surveys/services/add-survey-dialog.service';
import { EnableCookiesDialogService } from '@/surveys/services/enable-cookies-dialog.service';
import { NotificationOptDialogService } from '@/surveys/services/notification-opt-dialog.service';
import { NotificationOptDialogHttpService } from '@/surveys/services/notification-opt-dialog-http.service';
import { WelcomeSurveyDialogService } from '@/surveys/services/welcome-survey-dialog.service';
import { WelcomeSurveyDialogData } from '@/surveys/dialogs-data/welcome-survey-dialog-data';
import { EventService } from '@/surveys/services/event.service';
import { Component, OnDestroy, OnInit, OnChanges, SimpleChanges, Input, inject, signal, computed, DestroyRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppSettings, AppSettingsService } from '@/common/services/app-settings.service';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DecodeXmlModule } from '@/surveys/pipe/decode-xml/decode-xml.module';
import { ListViewInstanceComponent } from './list-view-instance/list-view-instance.component';

@Component({
    selector: 'app-list-view',
    templateUrl: './list-view.component.html',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        MatInputModule,
        MatTabsModule,
        MatIconModule,
        MatListModule,
        MatButtonModule,
        MatCardModule,
        MatTooltipModule,
        MatFormFieldModule,
        DecodeXmlModule,
        ListViewInstanceComponent
    ]
})
export class ListViewComponent extends SurveyViewsBaseComponent implements OnInit, OnDestroy, OnChanges {
    // Services using inject()
    private appSettingsService = inject(AppSettingsService);
    private ecorrUtilsService = inject(EcorrUtilsService);
    protected destroyRef = inject(DestroyRef);
    protected eventService = inject(EventService);
    protected paradataEventService = inject(ParadataEventService);
    protected enableCookiesDialogService = inject(EnableCookiesDialogService);

    // Signals for reactive state
    private _settings = signal<AppSettings>({ 
        pegaIntegration: true
    });
    private _surveys = signal<any[]>([]);
    private _active = signal<boolean>(false);
    private _reverse = signal<boolean>(true);
    private _order = signal<string>('displayName');

    // Computed signals
    currentSettings = computed(() => this._settings());
    currentSurveys = computed(() => this._surveys());
    isActive = computed(() => this._active());
    isReverse = computed(() => this._reverse());
    currentOrder = computed(() => this._order());

    // Properties that will be set by parent components
    @Input() surveys: any[] = [];
    @Input() active: boolean = false;

    // Legacy subscriptions for compatibility
    event$: Subscription;

    constructor() {
        super();
        this.event$ = this.eventService.eventListner()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(
                event => {
                    if (event.tabChanged) {
                        setTimeout(() => {
                            this.reverse.set(true);
                            this.order.set('displayName');
                            this.reorderSurveys(this.order());
                        }, 200);
                    }
                }
            );

        this.appSettingsService.getAppSettings()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe( settings => {
                if ( typeof settings === 'string' ) {
                      // Manually retyping to string then removing tainting and reparsing
                    this._settings.set(JSON.parse( JSON.stringify( settings ).slice( 8, -1 ).replace( /\\/g, '' ) ));
                }
            });
    }

    ngOnInit(): void {
        if (!navigator.cookieEnabled) {
            this.enableCookiesDialogService.open(this.enableCookiesInfo);
        }
        this.reverse.set(true);
        this.order.set('displayName');
        this.updateSurveys();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['surveys'] && changes['surveys'].currentValue) {
            this.updateSurveys();
        }
        if (changes['active'] && changes['active'].currentValue !== undefined) {
            this._active.set(this.active);
        }
    }

    private updateSurveys(): void {
        if (this.surveys && Array.isArray(this.surveys)) {
            this._surveys.set([...this.surveys]);
            this.reorderSurveys(this.order() || 'displayName');
        }
    }

    override reorderSurveys(attrib: string) {
        this.updateSortState(attrib);

        if (this.currentSurveys() && Array.isArray(this.currentSurveys())) {
            this._surveys.set(this.sortSurveyArray(this.currentSurveys(), attrib));
        }
    }

    fasClass(column: string) {
        if (this.order() === column) {
            if (this.reverse()) {
                return 'fa-sort-down';
            } else {
                return 'fa-sort-up';
            }
        } else {
            return 'fa-sort';
        }
    }

    ariaSort(column: string) {
        if (this.order() === column) {
            if (this.reverse()) {
                return 'descending';
            } else {
                return 'ascending';
            }
        } else {
            return 'none';
        }
    }

    isIE() {
        return this.ecorrUtilsService.isIE();
    }

    ngOnDestroy() {
        this.paradataEventService.flushParadataevents();
        // Subscriptions are now handled by takeUntilDestroyed, no manual unsubscribe needed here.
        super.destroy();
    }

}
